import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Printer } from 'lucide-react'

import { api, mensajeDeError } from '@/lib/api'
import { config } from '@/lib/config'
import { imprimirTicket } from '@/lib/imprimir'
import { Modal } from '@/componentes/ui/Modal'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'

interface Cierre {
  turnoId: number
  cerrado: boolean
  texto: string
  impreso?: boolean
}

interface Props {
  /** Sin id, el turno abierto o el último que cerró en esta caja. */
  turnoId?: number
  onCerrar: () => void
}

// El comprobante del turno.
//
// Existe por lo mismo que el ticket de una venta: el arqueo se ve en pantalla,
// pero lo que se archiva, se firma y se compara al otro día es un papel. Sin
// él, cualquier discusión sobre una diferencia depende de que alguien vuelva a
// abrir el sistema.
//
// El texto lo arma el SERVIDOR con el mismo armador en los dos hosts, así que
// el papel dice lo mismo salga de la térmica del mostrador o del navegador. Y
// respeta el arqueo a ciegas: si el cajero no puede ver el esperado en
// pantalla, el papel tampoco lo dice —si no, imprimir sería la puerta de atrás
// del control—.
export function DialogoCierre({ turnoId, onCerrar }: Props) {
  useAyudaDe('turno.cierre')

  const esCaja = config().runtime === 'caja'

  const [error, setError] = useState<string | null>(null)
  const [imprimiendo, setImprimiendo] = useState(false)

  const consulta = useQuery({
    queryKey: ['pos', 'cierre', turnoId ?? 'actual'],
    queryFn: () =>
      api.get<Cierre>(`/turno/cierre${turnoId ? `?turnoId=${turnoId}` : ''}`),
  })

  const cierre = consulta.data

  const imprimir = async () => {
    if (!cierre) return

    setError(null)

    // En la caja del mostrador imprime el HOST, que es el que tiene la
    // térmica; en el navegador, el diálogo de impresión. Es la misma decisión
    // que toma el ticket de una venta.
    if (esCaja) {
      setImprimiendo(true)

      try {
        const r = await api.get<Cierre>(
          `/turno/cierre?imprimir=true${turnoId ? `&turnoId=${turnoId}` : ''}`,
        )

        if (!r.impreso) {
          setError(
            'La impresora no respondió. El cierre está acá arriba: se puede volver a intentar.',
          )
        }
      } catch (e) {
        setError(mensajeDeError(e, 'No se pudo imprimir el cierre.'))
      } finally {
        setImprimiendo(false)
      }

      return
    }

    const salio = imprimirTicket(cierre.texto, 'Cierre de turno')

    if (!salio) {
      setError(
        'El navegador no dejó abrir la impresión. El cierre está acá arriba: se puede '
          + 'seleccionar y copiar, o imprimir la página con Ctrl+P.',
      )
    }
  }

  return (
    <Modal
      titulo={cierre?.cerrado === false ? 'Turno en curso' : 'Cierre de turno'}
      descripcion={
        cierre?.cerrado === false
          ? 'Es un parcial para controlar a mitad del día: no es el cierre.'
          : 'Lo que se archiva y se firma al terminar el turno.'
      }
      onCerrar={onCerrar}
      ancho="md"
      pie={
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCerrar} className="btn-secundario btn-sm">
            Listo
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => void imprimir()}
            disabled={!cierre || imprimiendo}
            className="btn-primario btn-sm"
          >
            {imprimiendo ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            Imprimir
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {consulta.isPending && (
          <div className="grid place-items-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-verde" />
          </div>
        )}

        {consulta.isError && (
          <p className="rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
            {mensajeDeError(consulta.error, 'No se pudo armar el cierre.')}
          </p>
        )}

        {/* El texto tal cual, en monoespaciada: es exactamente lo que sale por
            la térmica, así que revisar un cierre es mirar esta pantalla. */}
        {cierre && (
          <pre className="max-h-[420px] overflow-auto rounded-lg border border-borde bg-crema-alt px-3 py-3 text-[12px] leading-[1.45] text-tinta">
            {cierre.texto}
          </pre>
        )}

        {error && (
          <p className="rounded-lg bg-ambar/15 px-3 py-2.5 text-[13px] text-ambar">{error}</p>
        )}
      </div>
    </Modal>
  )
}
